import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import {
  CalendarClock,
  ChevronLeft,
  FileText,
  FolderOpen,
  Home,
  Loader2,
  LogOut,
  Mail,
  Menu,
  Phone,
  Receipt,
  Repeat,
  Star,
  User,
  Wrench,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Card, CardContent } from '@/components/atoms/card';
import { usePortalAuthStore } from '@/stores/portalAuth.store';
import { usePortalIdentity, usePortalLogout } from '@/hooks/portal/usePortalAuth';
import { usePortalHome } from '@/hooks/portal/usePortalHome';
import { ApiError } from '@/lib/api/errors';
import { officeContacts } from '@/lib/portal/officeContact';

interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Active only on an exact path match (Home), not on descendants. */
  exact?: boolean;
  /** Detail routes that belong to this navigation entry. */
  alsoActiveOn?: string;
  /** A pending-count pill (estimates awaiting / invoices due). */
  badge?: number;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

/** Up-to-two-letter initials for the brand mark ("Summit Air" → "SA"). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '·';
  return (parts[0]![0]! + (parts[1]?.[0] ?? '')).toUpperCase();
}

function PortalBrandMark({ name, logoUrl }: { name: string; logoUrl?: string | null }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  if (logoUrl && logoUrl !== failedUrl) {
    return (
      <img
        src={logoUrl}
        alt={`${name} logo`}
        className="size-9 shrink-0 rounded-lg object-contain"
        onError={() => setFailedUrl(logoUrl)}
      />
    );
  }
  return (
    <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-foreground text-xs font-semibold text-background">
      {initials(name)}
    </div>
  );
}

/**
 * The authenticated portal app-shell: a branded left sidebar (grouped nav +
 * pending badges + support), a top bar with sign-out (and a mobile menu), and a
 * single full-viewport scroll region for page content.
 *
 * The app's global CSS locks document scroll (`html,body,#root { overflow:hidden }`
 * for the dashboard), so the shell owns its own `overflow-y-auto` main — every
 * page taller than the viewport scrolls here, not on the document.
 *
 * Session policy lives here too: a revoked session (401/403 from `/me`) clears
 * local state and returns to login, while transient failures keep the session and
 * offer a retry. Page content is held until `/me` revalidates so cached data from
 * a revoked session can never flash.
 */
export function PortalShell({
  children,
  back,
  documentView = false,
}: {
  children: ReactNode;
  /** Full-width customer document with its own company rail; keep session validation/back. */
  documentView?: boolean;
  /**
   * Detail pages pass a back link (to their list); list pages rely on the nav.
   * `crumb` renders an uppercase breadcrumb (e.g. "EST-0002 · Furnace tune-up") in
   * place of the plain label, and `actions` fills the right of the bar (print /
   * download). Both optional so existing callers keep the simple back link.
   */
  back?: { label: string; to: string; crumb?: ReactNode; actions?: ReactNode };
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const identity = usePortalIdentity();
  const logout = usePortalLogout();
  const home = usePortalHome();
  const clear = usePortalAuthStore((s) => s.clear);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const isUnauthorized =
    identity.error instanceof ApiError &&
    (identity.error.status === 401 || identity.error.status === 403);
  useEffect(() => {
    if (isUnauthorized) {
      clear();
      void navigate({ to: '/portal/login' });
    }
  }, [isUnauthorized, clear, navigate]);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  async function onLogout() {
    await logout.mutateAsync().catch(() => undefined);
    await navigate({ to: '/portal/login' });
  }

  const tenantName = identity.data?.tenant.name ?? 'Customer Portal';
  const contacts = officeContacts(identity.data?.tenant);
  const alerts = home.data?.needsAttention ?? [];
  const estimateCount = alerts.filter((a) => a.kind === 'estimate').length;
  const invoiceCount = alerts.filter((a) => a.kind === 'invoice').length;

  const groups: NavGroup[] = [
    {
      label: 'Overview',
      items: [{ label: 'Home', to: '/portal', icon: Home, exact: true }],
    },
    {
      label: 'Money',
      items: [
        {
          label: 'Estimates',
          to: '/portal/estimates',
          icon: FileText,
          alsoActiveOn: '/portal/proposals',
          badge: estimateCount || undefined,
        },
        {
          label: 'Invoices',
          to: '/portal/invoices',
          icon: Receipt,
          badge: invoiceCount || undefined,
        },
      ],
    },
    {
      label: 'Your property',
      items: [
        { label: 'Service history', to: '/portal/visits', icon: CalendarClock },
        { label: 'Equipment', to: '/portal/equipment', icon: Wrench },
        { label: 'Membership', to: '/portal/memberships', icon: Star },
        { label: 'Recurring services', to: '/portal/plans', icon: Repeat },
        { label: 'Documents', to: '/portal/documents', icon: FolderOpen },
      ],
    },
    {
      label: 'You',
      items: [{ label: 'Account', to: '/portal/account', icon: User }],
    },
  ];

  const isActive = (item: NavItem) =>
    item.exact
      ? location.pathname === item.to
      : [item.to, item.alsoActiveOn].some(
          (path) =>
            path && (location.pathname === path || location.pathname.startsWith(`${path}/`)),
        );

  const sidebar = (
    <div className="flex h-full w-64 flex-col border-r border-border bg-background">
      <div className="flex items-center gap-3 border-b border-border px-4 py-4">
        <PortalBrandMark name={tenantName} logoUrl={identity.data?.tenant.logoUrl} />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{tenantName}</p>
          <p className="text-xs text-muted-foreground">Customer portal</p>
        </div>
        <button
          type="button"
          className="ml-auto rounded-md p-1 text-muted-foreground hover:bg-muted md:hidden"
          onClick={() => setMobileNavOpen(false)}
          aria-label="Close menu"
        >
          <X className="size-4" />
        </button>
      </div>

      <nav className="flex-1 space-y-5 overflow-y-auto p-3">
        {groups.map((group) => (
          <div key={group.label} className="space-y-1">
            <p className="px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {group.label}
            </p>
            {group.items.map((item) => {
              const active = isActive(item);
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors ${
                    active
                      ? 'bg-foreground font-medium text-background'
                      : 'text-foreground/80 hover:bg-muted'
                  }`}
                >
                  <Icon className="size-4 shrink-0" />
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.badge ? (
                    <span
                      className={`flex size-5 items-center justify-center rounded-full text-[11px] font-semibold ${
                        active
                          ? 'bg-background/20 text-background'
                          : 'bg-portal-accent text-portal-accent-foreground'
                      }`}
                    >
                      {item.badge}
                    </span>
                  ) : null}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="border-t border-border p-4">
        <p className="text-sm font-medium">Need a hand?</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Reach out to {tenantName} for anything about your service.
        </p>
        {contacts.length > 0 ? (
          <div className="mt-2 space-y-1">
            {contacts.map((contact) => {
              const Icon = contact.kind === 'phone' ? Phone : Mail;
              return (
                <a
                  key={contact.kind}
                  href={contact.href}
                  className="flex min-w-0 items-center gap-1.5 text-xs font-medium text-foreground hover:underline"
                >
                  <Icon className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate">{contact.value}</span>
                </a>
              );
            })}
          </div>
        ) : null}
      </div>
      <div className="border-t border-border p-2">
        <button
          type="button"
          onClick={onLogout}
          disabled={logout.isPending}
          className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-foreground/80 transition-colors hover:bg-muted disabled:opacity-60"
        >
          {logout.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <LogOut className="size-4" />
          )}
          Sign out
        </button>
        <p className="pt-1 text-center text-[11px] text-muted-foreground">Powered by Notifi</p>
      </div>
    </div>
  );

  return (
    <div className="flex h-dvh bg-canvas text-foreground">
      {/* Desktop sidebar */}
      {!documentView && <aside className="hidden md:block">{sidebar}</aside>}

      {/* Mobile drawer */}
      {mobileNavOpen ? (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 md:hidden"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden
          />
          <aside className="fixed inset-y-0 left-0 z-50 md:hidden">{sidebar}</aside>
        </>
      ) : null}

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* No desktop top bar — the sidebar is the chrome. A slim bar appears on
            mobile for the menu trigger, and at any width when a detail page needs
            a back link. */}
        <header
          className={`flex items-center gap-2 border-b border-border bg-background px-4 py-3 ${
            back ? '' : 'md:hidden'
          }`}
        >
          {back ? (
            <>
              <Link
                to={back.to}
                aria-label={back.label}
                className="flex shrink-0 items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <ChevronLeft className="size-4" />
                {back.crumb ? (
                  <span className="max-w-[52vw] truncate font-mono text-xs font-medium uppercase tracking-wider text-foreground/70 sm:max-w-none">
                    {back.crumb}
                  </span>
                ) : (
                  back.label
                )}
              </Link>
              {back.actions ? (
                <div className="ml-auto flex shrink-0 items-center gap-1">{back.actions}</div>
              ) : null}
            </>
          ) : (
            <>
              <button
                type="button"
                className="rounded-md p-1.5 text-muted-foreground hover:bg-muted"
                onClick={() => setMobileNavOpen(true)}
                aria-label="Open menu"
              >
                <Menu className="size-5" />
              </button>
              <PortalBrandMark name={tenantName} logoUrl={identity.data?.tenant.logoUrl} />
              <span className="min-w-0 truncate text-sm font-semibold">{tenantName}</span>
            </>
          )}
        </header>

        <main className="flex-1 overflow-y-auto">
          <div
            className={documentView ? 'w-full' : 'mx-auto w-full max-w-4xl space-y-4 p-4 sm:p-6'}
          >
            {identity.isPending || isUnauthorized ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
              </div>
            ) : identity.isError ? (
              <Card>
                <CardContent className="space-y-3 py-6 text-sm text-muted-foreground">
                  <p>Something went wrong on our end. Your session is still active.</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void identity.refetch()}
                    disabled={identity.isFetching}
                  >
                    {identity.isFetching ? <Loader2 className="size-4 animate-spin" /> : null}
                    Try again
                  </Button>
                </CardContent>
              </Card>
            ) : (
              children
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
